import { idsMatchSkills } from "../guards";
import { MAX_SELECTED_SKILLS } from "../types";
import type { SkillDecision, SkillDecisionSkill, SkillsResolveResponse } from "../types";

export const SKILL_RUN_ERRORS = {
  couldNotStop: "Could not stop the current run. Try again.",
  couldNotStart: "Could not start the replacement run. Try again.",
} as const;

function listLength(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

/** A continuation that asks for anything is not a matching-skill decision. */
export function continuationBlocksSkillDecision(
  continuation: Record<string, unknown> | undefined
): boolean {
  if (!continuation) return false;
  return (
    continuation.action !== "none" ||
    listLength(continuation.skill_ids) > 0 ||
    listLength(continuation.skills) > 0 ||
    listLength(continuation.candidates) > 0
  );
}

/**
 * Stop the current run, remove its user message, then submit the prompt again.
 * Hosts supply the abort and the submit; the order is the part both chats share.
 */
export async function replaceSkillRun(input: {
  stop: () => void;
  removeLastUserMessage: () => void;
  resubmit: () => Promise<boolean>;
}): Promise<true> {
  input.stop();
  input.removeLastUserMessage();
  await Promise.resolve();
  const accepted = await input.resubmit();
  if (!accepted) {
    throw new Error(SKILL_RUN_ERRORS.couldNotStart);
  }
  return true;
}

const toDecisionSkill = (skill: SkillDecisionSkill): SkillDecisionSkill =>
  Object.freeze({
    id: skill.id,
    name: skill.name,
    slug: skill.slug,
    origin: skill.origin,
    description: skill.description,
  });

/**
 * Map a version-1 skills resolve response onto the chat matching-skill decision.
 * Older bodies that omit disposition / recommended / continuation keep the
 * previous no-banner behavior.
 */
export function mapSkillDecisionFromResolveResponse(
  response: SkillsResolveResponse
): SkillDecision | null {
  const recommendedSkillIds = response.recommended_skill_ids ?? [];
  const recommendedSkills = response.recommended_skills ?? [];

  if (continuationBlocksSkillDecision(response.continuation)) {
    return null;
  }

  if (response.source !== "inferred") {
    return null;
  }

  if (
    response.disposition === "applied" &&
    response.skill_ids.length > 0 &&
    recommendedSkillIds.length === 0 &&
    recommendedSkills.length === 0 &&
    response.skills.every(({ origin }) => origin === "personal") &&
    idsMatchSkills(response.skill_ids, response.skills)
  ) {
    return {
      kind: "usingMatchingSkill",
      skills: response.skills.map(toDecisionSkill),
    };
  }

  if (
    response.disposition === "recommended" &&
    response.skill_ids.length === 0 &&
    response.skills.length === 0 &&
    recommendedSkillIds.length > 0 &&
    recommendedSkills.every(
      ({ origin }) => origin === "organization" || origin === "finster"
    ) &&
    idsMatchSkills(recommendedSkillIds, recommendedSkills)
  ) {
    return {
      kind: "recommended",
      skills: recommendedSkills.map(toDecisionSkill).slice(0, MAX_SELECTED_SKILLS),
    };
  }

  return null;
}

/**
 * Messages that remain after dropping the latest user turn and anything after it.
 * `null` means there was no user message to drop.
 */
export function messagesBeforeLastUser<T extends { role: string }>(
  messages: readonly T[]
): T[] | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === "user") {
      return messages.slice(0, index);
    }
  }
  return null;
}
